import React from 'react';
import { Link } from 'react-router-dom';
import { Scale, Building2, FileText, Award, ArrowRight, Search, BadgeCheck } from 'lucide-react';

/**
 * Per-context professional resource section.
 *
 * Drop this into any Guide page (Buying, Selling, Mortgage, Renting).
 * It renders a grid of deep-links into /find-a-professional?country=X&type=Y
 * so buyers/sellers/tenants land directly on the right specialist set
 * in their country.
 *
 * context: 'buying' | 'selling' | 'mortgage' | 'renting'
 * country: 'UK' | 'IN' | 'CA' | 'US'
 */

// Country × context -> list of {type_key, label, desc, icon}
const RESOURCES_MATRIX = {
  UK: {
    buying: [
      { key: 'solicitor', label: 'Conveyancing Solicitor', desc: 'Handles searches, contracts and completion.', icon: Scale },
      { key: 'rics_surveyor', label: 'RICS Chartered Surveyor', desc: 'Independent survey + valuation for your mortgage.', icon: FileText },
      { key: 'mortgage_broker', label: 'Mortgage Broker (FCA)', desc: 'Compares lenders and secures your mortgage offer.', icon: Award },
      { key: 'estate_agent', label: 'Estate Agent (NAEA/ARLA)', desc: 'Handles viewings, offers and negotiations.', icon: Building2 },
    ],
    selling: [
      { key: 'estate_agent', label: 'Estate Agent (NAEA/ARLA)', desc: 'Full-service listing, viewings and offer handling.', icon: Building2 },
      { key: 'solicitor', label: 'Conveyancing Solicitor', desc: 'Prepares the contract pack and manages completion.', icon: Scale },
      { key: 'rics_surveyor', label: 'RICS Surveyor', desc: 'Provides a formal RICS valuation for the listing.', icon: FileText },
    ],
    mortgage: [
      { key: 'mortgage_broker', label: 'Mortgage Broker (FCA)', desc: 'Whole-of-market lender access + protection advice.', icon: Award },
      { key: 'solicitor', label: 'Conveyancing Solicitor', desc: 'Needed at exchange/completion for any mortgage.', icon: Scale },
      { key: 'rics_surveyor', label: 'RICS Surveyor', desc: 'Lender-ready property valuation.', icon: FileText },
    ],
    renting: [
      { key: 'estate_agent', label: 'Letting Agent (ARLA)', desc: 'Sources and manages rental tenancies.', icon: Building2 },
      { key: 'solicitor', label: 'Property Solicitor', desc: 'Review tenancy agreements + deposit protection queries.', icon: Scale },
    ],
  },
  IN: {
    buying: [
      { key: 'advocate', label: 'Property Advocate', desc: 'Title verification, registration and agreement drafting.', icon: Scale },
      { key: 'rera_agent', label: 'RERA Estate Agent', desc: 'Handles your purchase and negotiates the deal.', icon: Building2 },
      { key: 'ca', label: 'Chartered Accountant', desc: 'Tax implications, TDS, capital-gains planning.', icon: Award },
      { key: 'valuer', label: 'IBBI Registered Valuer', desc: 'Independent property valuation.', icon: FileText },
    ],
    selling: [
      { key: 'rera_agent', label: 'RERA Estate Agent', desc: 'Lists and markets the property to verified buyers.', icon: Building2 },
      { key: 'advocate', label: 'Property Advocate', desc: 'Sale deed drafting + registration support.', icon: Scale },
      { key: 'ca', label: 'Chartered Accountant', desc: 'Capital-gains planning, 54/54EC reinvestment guidance.', icon: Award },
    ],
    mortgage: [
      { key: 'advocate', label: 'Property Advocate', desc: 'Title search + loan-agreement review.', icon: Scale },
      { key: 'valuer', label: 'IBBI Registered Valuer', desc: 'Bank-ready property valuation.', icon: FileText },
      { key: 'ca', label: 'Chartered Accountant', desc: 'Loan-eligibility calculation and tax optimisation.', icon: Award },
    ],
    renting: [
      { key: 'rera_agent', label: 'RERA Letting Agent', desc: 'Finds and vets tenants for your property.', icon: Building2 },
      { key: 'advocate', label: 'Property Advocate', desc: 'Registered rent agreement drafting.', icon: Scale },
    ],
  },
  CA: {
    buying: [
      { key: 'realtor', label: 'Realtor (RECO/BCFSA)', desc: 'Represents you through offer + closing.', icon: Building2 },
      { key: 'real_estate_lawyer', label: 'Real-Estate Lawyer', desc: 'Mandatory for Canadian property closings.', icon: Scale },
      { key: 'appraiser', label: 'AIC Appraiser', desc: 'Lender-ready valuation reports.', icon: FileText },
    ],
    selling: [
      { key: 'realtor', label: 'Realtor', desc: 'Lists, markets and negotiates.', icon: Building2 },
      { key: 'real_estate_lawyer', label: 'Real-Estate Lawyer', desc: 'Closing documents, title transfer.', icon: Scale },
    ],
    mortgage: [
      { key: 'real_estate_lawyer', label: 'Real-Estate Lawyer', desc: 'Registers the mortgage on title.', icon: Scale },
      { key: 'appraiser', label: 'AIC Appraiser', desc: 'Independent valuation for the lender.', icon: FileText },
    ],
    renting: [
      { key: 'realtor', label: 'Realtor (Leasing)', desc: 'Finds landlords or tenants in your province.', icon: Building2 },
      { key: 'notary', label: 'Notary Public', desc: 'Certifies tenancy or sub-lease documents.', icon: Scale },
    ],
  },
  US: {
    buying: [
      { key: 'realtor', label: 'Realtor (NAR)', desc: 'Buyer representation through to closing.', icon: Building2 },
      { key: 'attorney', label: 'Real-Estate Attorney', desc: 'Contract review + closing (mandatory in many states).', icon: Scale },
      { key: 'appraiser', label: 'Certified Appraiser', desc: 'Lender valuation of the property.', icon: FileText },
    ],
    selling: [
      { key: 'realtor', label: 'Realtor', desc: 'MLS listing + buyer negotiations.', icon: Building2 },
      { key: 'attorney', label: 'Real-Estate Attorney', desc: 'Title, deed and closing paperwork.', icon: Scale },
    ],
    mortgage: [
      { key: 'attorney', label: 'Real-Estate Attorney', desc: 'Loan + mortgage document review.', icon: Scale },
      { key: 'appraiser', label: 'Certified Appraiser', desc: 'ASC-licensed valuation report.', icon: FileText },
    ],
    renting: [
      { key: 'realtor', label: 'Realtor (Leasing)', desc: 'Sources and qualifies tenants.', icon: Building2 },
    ],
  },
};

const CONTEXT_TITLES = {
  buying: { eyebrow: 'Professionals you may need', heading: 'Buying a property? These verified specialists can help.' },
  selling: { eyebrow: 'Professionals you may need', heading: 'Selling a property? Build your support team.' },
  mortgage: { eyebrow: 'Professionals you may need', heading: 'Getting a mortgage? Meet the specialists.' },
  renting: { eyebrow: 'Professionals you may need', heading: 'Letting or renting? Verified specialists are one click away.' },
};

const ProfessionalResourcesSection = ({ context = 'buying', country = 'UK' }) => {
  const resources = RESOURCES_MATRIX[country]?.[context] || RESOURCES_MATRIX.UK[context] || [];
  const { eyebrow, heading } = CONTEXT_TITLES[context] || CONTEXT_TITLES.buying;

  if (resources.length === 0) return null;

  return (
    <section className="bg-white border border-slate-200 p-6 sm:p-8" data-testid={`pro-resources-${context}`}>
      <div className="mb-6">
        <p className="text-[11px] text-[#E07A3A] tracking-[0.3em] uppercase font-bold mb-2">{eyebrow}</p>
        <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl text-[#0F172A]">{heading}</h2>
        <p className="text-sm text-slate-500 mt-1">Every specialist is ID-checked and licence-verified with their statutory body.</p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {resources.map(({ key, label, desc, icon: Icon }) => (
          <Link
            key={key}
            to={`/find-a-professional?country=${country}&type=${key}`}
            className="group flex items-start gap-3 p-4 bg-[#F8FAFC] border border-slate-200 hover:border-[#E07A3A]/60 hover:bg-white transition-colors"
            data-testid={`pro-resource-${key}`}
          >
            <div className="w-10 h-10 bg-[#FEF3EC] border border-[#E07A3A]/30 flex items-center justify-center flex-shrink-0">
              <Icon className="h-5 w-5 text-[#E07A3A]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <p className="font-semibold text-[#0F172A] text-sm truncate">{label}</p>
                <BadgeCheck className="h-3.5 w-3.5 text-[#E07A3A] flex-shrink-0" title="Verified" />
              </div>
              <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{desc}</p>
              <span className="text-[11px] text-[#E07A3A] font-semibold mt-2 inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                Browse {country} → <ArrowRight className="h-3 w-3" />
              </span>
            </div>
          </Link>
        ))}
      </div>

      <Link to={`/find-a-professional?country=${country}`}
        className="mt-5 inline-flex items-center gap-2 text-sm text-[#0F172A] font-semibold hover:text-[#E07A3A] transition-colors"
        data-testid="pro-resource-view-all">
        <Search className="h-4 w-4" /> Browse the full directory →
      </Link>
    </section>
  );
};

export default ProfessionalResourcesSection;
